import { useState, useEffect } from "react";
import { api } from "@/api";
import { X, History, User, Calendar, Edit2, Trash2, Plus, Copy, Upload } from "lucide-react";

const ACTION_META = {
  create: { label: "Creation", color: "text-green-600 bg-green-50", icon: Plus },
  update: { label: "Modification", color: "text-blue-600 bg-blue-50", icon: Edit2 },
  delete: { label: "Suppression", color: "text-red-600 bg-red-50", icon: Trash2 },
  duplicate: { label: "Duplication", color: "text-emerald-600 bg-emerald-50", icon: Copy },
  import: { label: "Import CSV", color: "text-indigo-600 bg-indigo-50", icon: Upload },
};

export default function LineHistoryModal({ line, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/tax-lines/${line.id}/history`);
        setEntries(r.data?.entries || []);
      } catch { setEntries([]); }
      finally { setLoading(false); }
    })();
  }, [line.id]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="line-history-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-gray-50">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><History className="w-4 h-4 text-purple-600" /> Historique de la ligne</h3>
            <p className="text-xs text-gray-500 mt-0.5 font-mono">L.{line.line_number} — {line.label}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="close-history-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-10 text-center">
              <div className="w-5 h-5 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto" />
            </div>
          )}
          {!loading && !entries.length && (
            <div className="p-10 text-center text-gray-400 text-sm">
              <History className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Aucun historique disponible
            </div>
          )}
          {!loading && entries.map((e) => {
            const meta = ACTION_META[e.action] || { label: e.action, color: "text-gray-600 bg-gray-50", icon: History };
            const Icon = meta.icon;
            const changes = Object.keys(e.after || {})
              .map(k => ({ field: k, before: e.before?.[k], after: e.after?.[k] }))
              .filter(c => c.before !== c.after);
            return (
              <div key={e.id} className="px-5 py-3 border-b last:border-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${meta.color}`}>
                    <Icon className="w-3 h-3" /> {meta.label}
                  </span>
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <User className="w-3 h-3" /> {e.admin_email || "—"}
                  </span>
                  <span className="text-xs text-gray-400 flex items-center gap-1 ml-auto">
                    <Calendar className="w-3 h-3" /> {e.at ? new Date(e.at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" }) : ""}
                  </span>
                </div>
                {changes.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {changes.map((c, i) => (
                      <div key={i} className="text-xs bg-gray-50 rounded-lg px-3 py-1.5 flex items-center gap-2">
                        <span className="font-medium text-gray-700 min-w-[80px]">{c.field} :</span>
                        {c.before !== undefined && (
                          <span className="text-red-500 line-through font-mono">{String(c.before)}</span>
                        )}
                        <span className="text-gray-400">→</span>
                        <span className="text-green-600 font-mono">{String(c.after)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 text-right">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100">Fermer</button>
        </div>
      </div>
    </div>
  );
}
